import React, { useMemo, useCallback } from 'react';
import { Box, Paper, Title, Group } from '@mantine/core';
import { PieChart } from '@mui/x-charts/PieChart';
import LegendButton from './legend_button';

interface TriplePieChartProps {
  data: Record<string, Record<string, number>>;
}

const TriplePieChart: React.FC<TriplePieChartProps> = ({ data }) => {
  // Helper functions defined outside of render to prevent recreation
  const formatNumber = useCallback((num: number): number => {
    return Math.round(num * 100) / 100;
  }, []);

  const generateColors = useCallback((count: number): string[] => {
    const allColors: string[] = [
        "#335B74",
        "#1CADE4",
        "#2683C6",
        "#27CED7",
        "#42BA97",
        "#3E8853",
        "#62A39F",
        "#6EAC1C",
        "#3EBA53",
        "#285C73",  // Additional color
        "#90D3E5",  // Additional color
        "#107BBF",  // Additional color
        "#12C3B8",  // Additional color
        "#5EC587",  // Additional color
        "#46A367",  // Additional color
      ];
    const colors: string[] = [];
    
    for (let i = 0; i < count; i++) {
    colors.push(allColors[i % allColors.length]);
    }
    
    return colors;
  }, []);

  const calculatePercentage = useCallback((value: number, total: number): string => {
    const percentage = (value / total) * 100;
    return formatNumber(percentage).toString();
  }, [formatNumber]);

  // Memoize charts data with stable reference
  const charts = useMemo(() => {
    const chartKeys = Object.keys(data);
    return chartKeys.map((title) => {
      const chartData = data[title];
      const entries = Object.entries(chartData);
      const total = entries.reduce((sum, [_, value]) => sum + value, 0);
      const formattedTotal = formatNumber(total);
      
      const pieData = entries.map(([name, value], index) => ({
        id: index,
        value: formatNumber(value),
        label: `${name}: ${calculatePercentage(value, total)}%`,
        percentage: calculatePercentage(value, total)
      }));
      
      return {
        title,
        data: pieData,
        total: formattedTotal,
        colors: generateColors(pieData.length)
      };
    });
  }, [data, formatNumber, generateColors, calculatePercentage]);

  // Stable arc label function
  const getArcLabel = useCallback((item: any, chartData: any[]) => {
    const found = chartData.find((d) => d.id === item.id);
    if (found && parseFloat(found.percentage) > 5) {
      return `${found.percentage}%`;
    }
    return '';
  }, []);

  return (
    <Box style={{ width: '100%', height: '100%' }}>
      <Group grow align="stretch" style={{ height: '100%' }}>
        {charts.map((chart, index) => (
          <Paper 
            key={`${chart.title}-${index}`} 
            shadow="sm" 
            p="md" 
            radius="md" 
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <Title order={4} ta="center" mb="xs">
              {chart.title} ({chart.total})
            </Title>
            <Box style={{ flex: 1, minHeight: 0, position: 'relative' }}>
              <PieChart
                series={[
                  {
                    data: chart.data,
                    arcLabel: (item) => getArcLabel(item, chart.data),
                    arcLabelMinAngle: 35,
                  },
                ]}
                colors={chart.colors}
                height={350}
                margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                slotProps={{
                  legend: {
                    hidden: true,
                  },
                }}
                skipAnimation
              />
              <LegendButton
                modalTitle={chart.title}
                data={chart.data.reduce((acc, { label, value }) => {
                    acc[label] = value;
                    return acc;
                  }, {} as Record<string, number>)}
                colors={chart.colors}
              />
            </Box>
          </Paper>
        ))}
      </Group>
    </Box>
  );
};

export default TriplePieChart;